import { AppDomain } from '@pegasus/config';
import { Config } from 'src/config/types';

export type CmsAppdomain =
    | 'de'
    | 'fr'
    | 'nl'
    | 'benl'
    | 'befr'
    | 'at'
    | 'it'
    | 'pl'
    | 'es'
    | 'cz'
    | 'chde'
    | 'chfr'
    | 'fi'
    | 'dk'
    | 'se'
    | 'uk'
    | 'com'
    | 'no'
    | 'lt'
    | 'sk'
    | 'pt'
    | 'ro';

export const MAP_APPDOMAIN_CMS_APPDOMAIN: Record<AppDomain, CmsAppdomain> = {
    '17': 'de',
    '18': 'fr',
    '35': 'nl',
    '36': 'benl',
    '37': 'befr',
    '39': 'at',
    '40': 'chde',
    '41': 'chfr',
    '43': 'uk',
    '44': 'it',
    '45': 'pl',
    '46': 'es',
    '51': 'cz',
    '60': 'se',
    '61': 'dk',
    '62': 'fi',

    '63': 'no',
    '66': 'lt',
    '67': 'sk',
    '70': 'pt',
    '71': 'ro',
};

export interface StaticPage {
    fields: {
        title: string;
        meta: {
            fields: Record<string, any>;
        };
    } & Record<string, any>;
}

export type hreflangLink = {
    href: string;
    hreflang: string;
};

export type CMS<T> = {
    id: string;
    type: string;
    fields: T;
};

export interface CmsPageContent {
    staticPage?: StaticPage;
}

export type StaticPageResponse = StaticPage[];

export type Filter = (config: Config) => boolean;

export interface MenuItem {
    title: string;
    url: string;
    showDivider?: boolean;
    children?: MenuItem[];
    filter?: Filter;
}

const getJobsURL = (appdomain: AppDomain) => {
    const useDe = [17, 39, 40];
    const useFr = [18, 37, 41];
    if (useDe.includes(appdomain)) {
        return 'https://jobs.zalando.com/de/';
    }
    if (useFr.includes(appdomain)) {
        return 'https://jobs.zalando.com/fr/';
    }
    return 'https://jobs.zalando.com/en/';
};

const FilterPrelaunch: Filter = config => !config.enable.prelaunch;

export const getMenuItems = (config: Config): MenuItem[] => {
    return [
        {
            title: 'aboutUs',
            url: '/aboutus/',
            filter: FilterPrelaunch,
        },
        {
            title: 'help',
            url: '/help/',
            filter: FilterPrelaunch,
        },
        // {
        //     title: 'fanPages',
        //     url: '/fanpages',
        //     filter: FilterPrelaunch,
        // },
        {
            title: 'sizeGuide',
            url: '/sizehelper/',
            filter: FilterPrelaunch,
            children: [
                {
                    title: 'sizeGuideWomen',
                    url: '/sizehelper/women/',
                    children: [
                        { title: 'sizeGuideWomenMeasure', url: '/sizehelper/women/measure/' },
                        { title: 'sizeGuideWomenFigureTypes', url: '/sizehelper/women/figures/' },
                        {
                            title: 'sizeGuideWomenTrouserTypes',
                            url: '/sizehelper/women/trousertypes/',
                        },
                        { title: 'sizeGuideWomenDressTypes', url: '/sizehelper/women/dresstypes/' },
                        {
                            title: 'sizeGuideWomenUnderwearTypes',
                            url: '/sizehelper/women/underweartypes/',
                        },
                    ],
                },

                {
                    title: 'sizeGuideMen',
                    url: '/sizehelper/men/',
                    children: [
                        { title: 'sizeGuideMenMeasure', url: '/sizehelper/men/measure/' },
                        { title: 'sizeGuideMenFigureTypes', url: '/sizehelper/men/figures/' },
                        { title: 'sizeGuideMenTrouserTypes', url: '/sizehelper/men/trousertypes/' },
                        { title: 'sizeGuideMenBlazerTypes', url: '/sizehelper/men/blazertypes/' },
                    ],
                },

                {
                    title: 'sizeGuideChildren',
                    url: '/sizehelper/children/',
                },

                {
                    title: 'sizeGuideShoes',
                    url: '/sizehelper/shoes/',
                    children: [
                        { title: 'sizeGuideShoesMeasure', url: '/sizehelper/shoes/measure/' },
                        { title: 'sizeGuideShoesFigures', url: '/sizehelper/shoes/figures/' },
                    ],
                },
            ],
        },
        {
            title: 'contact',
            url: '/contact/',
            filter: FilterPrelaunch,
            showDivider: true,
        },
        {
            title: 'privacy',
            url: '/privacy/',
        },
        {
            title: 'impress',
            url: '/impress/',
        },
        {
            title: 'terms',
            url: '/terms/',
            filter: FilterPrelaunch,
            showDivider: true,
        },
        {
            title: 'jobs',
            filter: FilterPrelaunch,
            url: getJobsURL(config.appdomainId),
        },
    ].filter(({ filter = () => true }) => filter(config));
};

const flatten = (items: MenuItem[] = []): string[] => [
    ...items.map(item => item.url),
    ...items.reduce((result, item) => [...result, ...flatten(item.children)], []),
];

export const getPaths = (config: Config) =>
    flatten(getMenuItems(config)).filter(path => !path.startsWith('http'));

export const getPathsMeta = (config: Config) => getPaths(config).filter(path => path !== '/help');
